import {
  Alert,
  Button,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  TextField,
  Box,
  Typography,
  Paper,
} from '@mui/material';
import axios, { AxiosResponse, isAxiosError } from 'axios';
import { useContext, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API_ENDPOINT } from '../../configurations/global.config';
import { UserType } from '../../enums/users';
import { IResetPasswordFormData } from '../../interfaces/common/authentication';
import { formatAccountId, passwordValidationError } from './utils';
import { VerifyPhoneDialog } from './verify-phone-dialog';
import { format_phone_number } from '../../utils/phone_numbers';
import { MuiTelInput } from 'mui-tel-input';
import PasswordField from '../common/password-field/password-field';
import { isMobile, isTablet } from 'react-device-detect';
import { DomainForcedOrganisationContext } from '../../contexts/domain-forced-organisation-context';

const defaultFormData: IResetPasswordFormData = {
  userType: UserType.DRIVER,
  organisation_id:  '',
  password: '',
  confirmPassword: '',
  phone: '',
};

function ResetPasswordForm() {
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState<AxiosResponse>();
  const [formData, setFormData] =
    useState<IResetPasswordFormData>(defaultFormData);
  const [message, setMessage] = useState<string>('');
  const [verificationDialogOpen, setVerificationDialogOpen] =
    useState<boolean>(false);
  const {forcedOrganisation} = useContext(DomainForcedOrganisationContext)
  
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect_to') || '';

  useEffect(()=>{
    if (forcedOrganisation)
      setFormData({...formData, organisation_id: forcedOrganisation?.organisation_id})
  }, [forcedOrganisation])

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    setResponse(undefined);
    setMessage('');
    setIsLoading(true);

    const passwordError = passwordValidationError(
      formData.password,
      formData.confirmPassword
    );

    if (passwordError) {
      setMessage(passwordError);
    } else {
      const phone = format_phone_number(formData.phone);
      if (phone === '') {
        setMessage('Invalid phone number');
        setIsLoading(false);
        return;
      }
      const requestData = {
        account_id: formatAccountId(formData.phone, formData.userType, formData.organisation_id),
        phone_number: phone,
        password: formData.password,
        account_type: formData.userType,
      };

      try {
        const response = await axios.post(
          `${API_ENDPOINT}/api/auth/change-password/`,
          requestData
        );
        setResponse(response);

        setVerificationDialogOpen(true);
      } catch (e) {
        if (isAxiosError(e)) {
          const response = e.response;
          setResponse(response);
          if (response?.statusText === 'Not Found') {
            setMessage('This phone number is not associated to an account.');
          } else if (response?.data.message === 'account suspended') {
            setMessage(
              'Your account has been suspended because you entered a wrong verification code too many times. Please contact us to unblock your account.'
            );
          } else if (response?.data.message === 'too many requests') {
            setMessage(
              `Too many requests. Please wait 2 minutes and try again.`
            );
          } else {
            setMessage('Unexpected server error. Please contact us.');
          }
        } else {
          setMessage('Unexpected error. Please contact us.');
        }
      }
    }
    setIsLoading(false);
  };

  const closeVerificationDialog = () => {
    setVerificationDialogOpen(false);
  };

  return (
    <Paper
      elevation={3}
      sx={{
        maxWidth: 480,
        margin: 'auto',
        padding: 4,
        borderRadius: 3,
        boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
        backgroundColor: '#ffffff',
      }}
    >
      <Typography variant="h5" fontWeight={600} gutterBottom>
        Reset Password
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Please enter your details to reset your password
      </Typography>

      {message && (
        <Alert
          severity={
            response && response?.status >= 200 && response?.status < 400
              ? 'success'
              : 'error'
          }
          sx={{ mb: 2, borderRadius: 1 }}
        >
          {message}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <FormControl fullWidth sx={{ mt: 2 }}>
          <InputLabel>Profile</InputLabel>
          <Select
            value={formData.userType}
            label="Profile"
            required
            disabled={isLoading}
            onChange={(e) =>
              setFormData({ ...formData, userType: e.target.value as UserType })
            }
            variant="outlined"
            defaultValue={UserType.MERCHANT}
          >
            <MenuItem key={UserType.MERCHANT} value={UserType.MERCHANT}>
              Merchant
            </MenuItem>

            <MenuItem key={UserType.ORGANISATION} value={UserType.ORGANISATION}>
              Organisation
            </MenuItem>
          </Select>
        </FormControl>

        {['employee', 'merchant', 'driver'].includes(formData.userType) && !forcedOrganisation && (
          <FormControl fullWidth sx={{ mt: 2 }}>
            <TextField
              label="Delivery Organisation ID"
              type="text"
              value={formData.organisation_id}
              disabled={isLoading}
              required
              onChange={(e) =>
                setFormData({ ...formData, organisation_id: e.target.value})
              }
              variant="outlined"
            />
          </FormControl>
        )}

        <FormControl fullWidth sx={{ mt: 2 }}>
          <MuiTelInput
            forceCallingCode
            label="Phone Number"
            disabled={isLoading}
            defaultCountry={'LB'}
            variant="outlined"
            required
            value={formData.phone}
            onChange={(newPhone) => {
              if (typeof newPhone === 'string') {
                setFormData({ ...formData, phone: newPhone });
              }
            }}
          />
        </FormControl>
        <Box sx={{ mt: 2 }}>
          <PasswordField
            withConfirmation={true}
            label="New Password"
            confirmLabel="Confirm New Password"
            placeholder="Enter a new password"
            value={formData.password}
            confirmValue={formData.confirmPassword}
            onPasswordChange={(value) =>
              setFormData({ ...formData, password: value })
            }
            onConfirmChange={(value) =>
              setFormData({ ...formData, confirmPassword: value })
            }
            variant="outlined"
          />
        </Box>

        {isLoading && <LinearProgress sx={{ mt: 2 }} />}

        <Box display="flex" gap={2} mt={4}>
          <Button
            fullWidth
            variant="contained"
            type="submit"
            sx={{ borderRadius: 2, fontWeight: 600 }}
            disabled={isLoading}
          >
            Update Password
          </Button>

          <Button
            fullWidth
            variant="outlined"
            onClick={() => navigate(`/login${redirectTo ? `?redirect_to=${encodeURIComponent(redirectTo)}` : ''}`)}
            sx={{ borderRadius: 2 }}
            disabled={isLoading}
          >
            Back to Login
          </Button>
        </Box>
      </form>

     {verificationDialogOpen && <VerifyPhoneDialog
        isOpen={true}
        closeVerificationDialog={closeVerificationDialog}
        phoneNumber={formData.phone}
        password={formData.password}
        account_id={formatAccountId(formData.phone, formData.userType, formData.organisation_id)}
        redirectTo={redirectTo}
      />
     }
    </Paper>
  );
}

export default ResetPasswordForm;